import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';

/* ── Icon components ──────────────────────────────────── */
const SearchIcon = ({ size = 16, color = 'currentColor' }) => (
  <svg width={size} height={size} fill="none" stroke={color} strokeWidth="2" viewBox="0 0 24 24">
    <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);

const SpinnerIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--primary)" strokeWidth="2.5" style={{ animation: 'cp-spin 0.7s linear infinite' }}>
    <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
  </svg>
);

const ArrowIcon = () => (
  <svg width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
    <polyline points="9 18 15 12 9 6" />
  </svg>
);

/* ── Type config ──────────────────────────────────────── */
const TYPE_CONFIG = {
  client:   { label: 'Clients',    color: '#1d4ed8', bg: '#dbeafe', icon: ClientIcon   },
  customer: { label: 'Customers',  color: '#0d9488', bg: '#ccfbf1', icon: CustomerIcon },
  catalog:  { label: 'Catalog',    color: '#0891b2', bg: '#cffafe', icon: CatalogIcon  },
  order:    { label: 'Orders',     color: '#16a34a', bg: '#dcfce7', icon: OrderIcon    },
  inquiry:  { label: 'Inquiries',  color: '#ea580c', bg: '#ffedd5', icon: InquiryIcon  },
  project:  { label: 'Projects',   color: '#7c3aed', bg: '#ede9fe', icon: ProjectIcon  },
};

function ClientIcon({ color }) {
  return (
    <svg width="14" height="14" fill="none" stroke={color} strokeWidth="2" viewBox="0 0 24 24">
      <rect x="2" y="7" width="20" height="14" rx="2" /><path d="M16 7V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v2" />
    </svg>
  );
}
function CustomerIcon({ color }) {
  return (
    <svg width="14" height="14" fill="none" stroke={color} strokeWidth="2" viewBox="0 0 24 24">
      <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" /><circle cx="12" cy="7" r="4" />
    </svg>
  );
}
function CatalogIcon({ color }) {
  return (
    <svg width="14" height="14" fill="none" stroke={color} strokeWidth="2" viewBox="0 0 24 24">
      <path d="M9 3H5a2 2 0 00-2 2v4m6-6h10a2 2 0 012 2v4M9 3v18m0 0h10a2 2 0 002-2V9M9 21H5a2 2 0 01-2-2V9m0 0h18" />
    </svg>
  );
}
function OrderIcon({ color }) {
  return (
    <svg width="14" height="14" fill="none" stroke={color} strokeWidth="2" viewBox="0 0 24 24">
      <path d="M20 7l-8-4-8 4m16 0v10l-8 4m-8-4V7m16 0L12 11M4 7l8 4" />
    </svg>
  );
}
function InquiryIcon({ color }) {
  return (
    <svg width="14" height="14" fill="none" stroke={color} strokeWidth="2" viewBox="0 0 24 24">
      <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" />
    </svg>
  );
}
function ProjectIcon({ color }) {
  return (
    <svg width="14" height="14" fill="none" stroke={color} strokeWidth="2" viewBox="0 0 24 24">
      <rect x="3" y="3" width="18" height="18" rx="2" /><line x1="3" y1="9" x2="21" y2="9" /><line x1="9" y1="21" x2="9" y2="9" />
    </svg>
  );
}

/* ── Quick nav links shown when search is empty ───────── */
const QUICK_LINKS = [
  { label: 'Dashboard',    link: '/admin/dashboard',  icon: DashIcon,    color: '#1d4ed8', desc: 'Go to dashboard' },
  { label: 'New Inquiry',  link: '/admin/inquiries',  icon: InquiryIcon, color: '#ea580c', desc: 'Create inquiry'   },
  { label: 'Catalog',      link: '/admin/catalog',    icon: CatalogIcon, color: '#0891b2', desc: 'Browse catalog'  },
  { label: 'Clients',      link: '/admin/clients',    icon: ClientIcon,  color: '#1d4ed8', desc: 'View clients'    },
  { label: 'Customers',    link: '/admin/customers',  icon: CustomerIcon,color: '#0d9488', desc: 'View customers'  },
  { label: 'Orders',       link: '/admin/orders',     icon: OrderIcon,   color: '#16a34a', desc: 'View orders'     },
  { label: 'HR',           link: '/admin/hr',         icon: HRIcon,      color: '#7c3aed', desc: 'HR module'       },
  { label: 'Analytics',    link: '/admin/analytics',  icon: AnalyticsIcon, color: '#0284c7', desc: 'View analytics' },
];

function DashIcon({ color }) {
  return (
    <svg width="14" height="14" fill="none" stroke={color} strokeWidth="2" viewBox="0 0 24 24">
      <rect x="3" y="3" width="7" height="7" rx="1" /><rect x="14" y="3" width="7" height="7" rx="1" />
      <rect x="14" y="14" width="7" height="7" rx="1" /><rect x="3" y="14" width="7" height="7" rx="1" />
    </svg>
  );
}
function HRIcon({ color }) {
  return (
    <svg width="14" height="14" fill="none" stroke={color} strokeWidth="2" viewBox="0 0 24 24">
      <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" /><circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75" />
    </svg>
  );
}
function AnalyticsIcon({ color }) {
  return (
    <svg width="14" height="14" fill="none" stroke={color} strokeWidth="2" viewBox="0 0 24 24">
      <line x1="18" y1="20" x2="18" y2="10" /><line x1="12" y1="20" x2="12" y2="4" />
      <line x1="6" y1="20" x2="6" y2="14" />
    </svg>
  );
}

/* ── Highlight matching text ──────────────────────────── */
function HighlightText({ text, query }) {
  if (!query || !text) return <span>{text}</span>;
  const idx = text.toLowerCase().indexOf(query.toLowerCase());
  if (idx === -1) return <span>{text}</span>;
  return (
    <span>
      {text.slice(0, idx)}
      <mark style={{ background: '#fef08a', color: '#713f12', borderRadius: 2, padding: '0 1px' }}>
        {text.slice(idx, idx + query.length)}
      </mark>
      {text.slice(idx + query.length)}
    </span>
  );
}

/* ── Group results by type ────────────────────────────── */
function groupResults(results) {
  const groups = {};
  results.forEach(r => {
    if (!groups[r.type]) groups[r.type] = [];
    groups[r.type].push(r);
  });
  return groups;
}

/* ── Main component ───────────────────────────────────── */
export default function CommandPalette() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);
  const navigate = useNavigate();

  const inputRef = useRef(null);
  const listRef = useRef(null);
  const debounceTimer = useRef(null);

  /* ── Open/close via Ctrl+K ── */
  useEffect(() => {
    const handler = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        setOpen(prev => !prev);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  /* ── Auto-focus input when opened ── */
  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 40);
      setQuery('');
      setResults([]);
      setActiveIdx(0);
    }
  }, [open]);

  /* ── Debounced search ── */
  const doSearch = useCallback((q) => {
    clearTimeout(debounceTimer.current);
    if (!q || q.length < 2) {
      setResults([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    debounceTimer.current = setTimeout(async () => {
      try {
        const { data: res } = await api.get('/search', { params: { q } });
        setResults(res.data || []);
        setActiveIdx(0);
      } catch {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 250);
  }, []);

  const handleInput = (e) => {
    const val = e.target.value;
    setQuery(val);
    doSearch(val);
  };

  /* ── Flat navigable list ── */
  const flatList = query.length >= 2
    ? results
    : QUICK_LINKS.map(q => ({ ...q, type: '_quick' }));

  /* ── Keyboard navigation ── */
  const handleKeyDown = (e) => {
    if (e.key === 'Escape') { setOpen(false); return; }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIdx(i => Math.min(i + 1, flatList.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIdx(i => Math.max(i - 1, 0));
    } else if (e.key === 'Enter' && flatList[activeIdx]) {
      e.preventDefault();
      navigateTo(flatList[activeIdx].link);
    }
  };

  /* ── Scroll active item into view ── */
  useEffect(() => {
    const el = listRef.current?.querySelector(`[data-idx="${activeIdx}"]`);
    el?.scrollIntoView({ block: 'nearest' });
  }, [activeIdx]);

  const navigateTo = (link) => {
    navigate(link);
    setOpen(false);
  };

  /* ── Grouped results for display ── */
  const groups = query.length >= 2 ? groupResults(results) : null;

  if (!open) return null;

  return (
    <>
      {/* ── Backdrop ── */}
      <div
        onClick={() => setOpen(false)}
        style={{
          position: 'fixed', inset: 0, background: 'rgba(10,22,40,0.55)',
          backdropFilter: 'blur(3px)', zIndex: 9998,
          animation: 'cp-fadeIn 0.15s ease',
        }}
      />

      {/* ── Modal card ── */}
      <div
        style={{
          position: 'fixed', top: '12%', left: '50%', transform: 'translateX(-50%)',
          width: '100%', maxWidth: 580, zIndex: 9999,
          animation: 'cp-scaleIn 0.18s cubic-bezier(0.34,1.2,0.64,1)',
        }}
      >
        <div style={{
          background: '#fff', borderRadius: 14,
          boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25), 0 0 0 1px rgba(0,0,0,0.06)',
          overflow: 'hidden', display: 'flex', flexDirection: 'column',
          maxHeight: '70vh',
        }}>

          {/* ── Search input row ── */}
          <div style={{
            display: 'flex', alignItems: 'center', gap: 10, padding: '14px 16px',
            borderBottom: '1px solid #e2e8f0',
          }}>
            <span style={{ flexShrink: 0, color: '#94a3b8', display: 'flex' }}>
              {loading ? <SpinnerIcon /> : <SearchIcon size={17} color="#94a3b8" />}
            </span>
            <input
              ref={inputRef}
              value={query}
              onChange={handleInput}
              onKeyDown={handleKeyDown}
              placeholder="Search clients, orders, catalog, inquiries…"
              style={{
                flex: 1, border: 'none', outline: 'none', fontSize: '0.975rem',
                color: '#0f172a', background: 'transparent',
                fontFamily: 'inherit', caretColor: '#1d4ed8',
              }}
            />
            <kbd style={{
              flexShrink: 0, padding: '2px 7px', fontSize: '0.7rem', fontFamily: 'inherit',
              color: '#94a3b8', background: '#f1f5f9', border: '1px solid #e2e8f0',
              borderRadius: 5, fontWeight: 600, letterSpacing: '0.02em',
            }}>ESC</kbd>
          </div>

          {/* ── Results / Quick nav ── */}
          <div ref={listRef} style={{ overflowY: 'auto', flex: 1, padding: '8px 0' }}>

            {/* Empty query → quick nav */}
            {query.length < 2 && (
              <>
                <div style={{
                  padding: '4px 16px 6px', fontSize: '0.7rem', fontWeight: 700,
                  color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.08em',
                }}>
                  Quick Navigation
                </div>
                {QUICK_LINKS.map((item, i) => {
                  const Ico = item.icon;
                  const isActive = activeIdx === i;
                  return (
                    <button
                      key={item.link}
                      data-idx={i}
                      onClick={() => navigateTo(item.link)}
                      onMouseEnter={() => setActiveIdx(i)}
                      style={{
                        width: '100%', padding: '9px 16px', textAlign: 'left',
                        background: isActive ? '#f0f7ff' : 'transparent',
                        border: 'none', cursor: 'pointer', display: 'flex',
                        alignItems: 'center', gap: 12, transition: 'background 0.1s',
                      }}
                    >
                      <span style={{
                        width: 30, height: 30, borderRadius: 8, flexShrink: 0,
                        background: item.color + '18', display: 'flex',
                        alignItems: 'center', justifyContent: 'center',
                      }}>
                        <Ico color={item.color} />
                      </span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: '0.875rem', fontWeight: 600, color: '#0f172a' }}>
                          {item.label}
                        </div>
                        <div style={{ fontSize: '0.75rem', color: '#94a3b8' }}>{item.desc}</div>
                      </div>
                      {isActive && (
                        <span style={{ color: '#94a3b8', display: 'flex', flexShrink: 0 }}>
                          <ArrowIcon />
                        </span>
                      )}
                    </button>
                  );
                })}
              </>
            )}

            {/* Has query, loading */}
            {query.length >= 2 && loading && (
              <div style={{
                padding: '36px 16px', textAlign: 'center',
                color: '#94a3b8', fontSize: '0.875rem',
                display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10,
              }}>
                <SpinnerIcon />
                <span>Searching…</span>
              </div>
            )}

            {/* Has query, no results */}
            {query.length >= 2 && !loading && results.length === 0 && (
              <div style={{
                padding: '40px 16px', textAlign: 'center',
                color: '#94a3b8', fontSize: '0.875rem',
              }}>
                <div style={{ fontSize: '2rem', marginBottom: 8 }}>🔍</div>
                <div style={{ fontWeight: 600, color: '#475569', marginBottom: 4 }}>No results found</div>
                <div>Try a different keyword or browse using Quick Navigation</div>
              </div>
            )}

            {/* Has query, grouped results */}
            {query.length >= 2 && !loading && results.length > 0 && (
              (() => {
                let globalIdx = 0;
                return Object.entries(groups).map(([type, items]) => {
                  const cfg = TYPE_CONFIG[type] || { label: type, color: '#64748b', bg: '#f1f5f9' };
                  const Ico = cfg.icon || (() => null);
                  return (
                    <div key={type}>
                      <div style={{
                        padding: '8px 16px 4px', fontSize: '0.68rem', fontWeight: 700,
                        color: cfg.color, textTransform: 'uppercase', letterSpacing: '0.08em',
                        display: 'flex', alignItems: 'center', gap: 6,
                      }}>
                        <span style={{
                          width: 16, height: 16, display: 'inline-flex',
                          alignItems: 'center', justifyContent: 'center',
                        }}>
                          <Ico color={cfg.color} />
                        </span>
                        {cfg.label}
                        <span style={{
                          marginLeft: 2, background: cfg.bg, color: cfg.color,
                          borderRadius: 10, padding: '0 6px', fontWeight: 700, fontSize: '0.65rem',
                        }}>{items.length}</span>
                      </div>
                      {items.map((item) => {
                        const idx = globalIdx++;
                        const isActive = activeIdx === idx;
                        return (
                          <button
                            key={`${type}-${item.id}`}
                            data-idx={idx}
                            onClick={() => navigateTo(item.link)}
                            onMouseEnter={() => setActiveIdx(idx)}
                            style={{
                              width: '100%', padding: '8px 16px', textAlign: 'left',
                              background: isActive ? '#f0f7ff' : 'transparent',
                              border: 'none', cursor: 'pointer', display: 'flex',
                              alignItems: 'center', gap: 12, transition: 'background 0.1s',
                            }}
                          >
                            <span style={{
                              width: 30, height: 30, borderRadius: 8, flexShrink: 0,
                              background: cfg.bg, display: 'flex',
                              alignItems: 'center', justifyContent: 'center',
                            }}>
                              <Ico color={cfg.color} />
                            </span>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{
                                fontSize: '0.875rem', fontWeight: 600, color: '#0f172a',
                                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                              }}>
                                <HighlightText text={item.title} query={query} />
                              </div>
                              {item.subtitle && (
                                <div style={{
                                  fontSize: '0.75rem', color: '#64748b',
                                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                }}>
                                  <HighlightText text={item.subtitle} query={query} />
                                </div>
                              )}
                            </div>
                            <span style={{
                              fontSize: '0.65rem', padding: '2px 7px', borderRadius: 8,
                              background: cfg.bg, color: cfg.color,
                              fontWeight: 700, textTransform: 'capitalize', flexShrink: 0,
                            }}>
                              {cfg.label.slice(0, -1)}
                            </span>
                            {isActive && (
                              <span style={{ color: '#94a3b8', display: 'flex', flexShrink: 0 }}>
                                <ArrowIcon />
                              </span>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  );
                });
              })()
            )}
          </div>

          {/* ── Footer hints ── */}
          <div style={{
            borderTop: '1px solid #e2e8f0', padding: '8px 16px',
            display: 'flex', alignItems: 'center', gap: 16,
            background: '#f8fafc', flexWrap: 'wrap',
          }}>
            {[
              { keys: ['↑', '↓'], label: 'navigate' },
              { keys: ['↵'],      label: 'select'   },
              { keys: ['Esc'],     label: 'close'    },
            ].map(({ keys, label }) => (
              <span key={label} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                {keys.map(k => (
                  <kbd key={k} style={{
                    padding: '1px 5px', fontSize: '0.68rem', fontFamily: 'inherit',
                    color: '#475569', background: '#fff', border: '1px solid #e2e8f0',
                    borderRadius: 4, fontWeight: 600, boxShadow: '0 1px 0 #e2e8f0',
                  }}>{k}</kbd>
                ))}
                <span style={{ fontSize: '0.7rem', color: '#94a3b8' }}>{label}</span>
              </span>
            ))}
            <span style={{ marginLeft: 'auto', fontSize: '0.7rem', color: '#94a3b8' }}>
              Searching across clients, customers, catalog, orders, inquiries &amp; projects
            </span>
          </div>
        </div>
      </div>

      {/* ── Keyframe animations ── */}
      <style>{`
        @keyframes cp-fadeIn  { from { opacity: 0 } to { opacity: 1 } }
        @keyframes cp-scaleIn { from { opacity: 0; transform: translateX(-50%) scale(0.95) } to { opacity: 1; transform: translateX(-50%) scale(1) } }
        @keyframes cp-spin    { to { transform: rotate(360deg) } }
      `}</style>
    </>
  );
}
